// Lightweight document-head SEO helper for client-rendered pages.
// Sets title, meta description, canonical, optional robots noindex, and JSON-LD,
// and returns a cleanup that restores the title and removes anything it created.
//
// The app is a CRA SPA with no SSR, so canonical / JSON-LD / noindex are injected
// client-side here. Modern Googlebot renders JS and reads them after render. The
// server-side OG proxy (GET /api/og) covers the non-JS social crawlers only.

import i18n from "@/i18n";

export const SITE_URL = process.env.REACT_APP_SITE_URL || "https://samtwoentertainment.com";

export const canonicalFor = (path) => {
  const clean = (path || "/").split("?")[0].split("#")[0];
  if (clean === "/" || clean === "") return SITE_URL;
  return `${SITE_URL}${clean.replace(/\/+$/, "")}`;
};

// Search/social descriptions: one line, about 158 characters, cut at a word boundary.
export const seoDescription = (text) => {
  const flat = (text || "").replace(/\s+/g, " ").trim();
  if (flat.length <= 158) return flat || undefined;
  const cut = flat.slice(0, 157);
  return `${cut.slice(0, cut.lastIndexOf(" ") > 100 ? cut.lastIndexOf(" ") : 157).replace(/[\s.,;:!-]+$/, "")}…`;
};

export const applySeo = ({ title, description: rawDescription, canonical, noindex, jsonLd } = {}) => {
  const description = seoDescription(rawDescription);
  const prevTitle = document.title;
  if (title) document.title = title;

  const created = [];

  const setMeta = (name, content) => {
    if (!content) return;
    let el = document.head.querySelector(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", name);
      document.head.appendChild(el);
      created.push(el);
    }
    el.setAttribute("content", content);
  };

  setMeta("description", description);
  if (noindex) setMeta("robots", "noindex, nofollow");

  // Social/OG tags mirror the page title and description; previous values come back on cleanup.
  const restored = [];
  const setTag = (attr, key, content) => {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
      created.push(el);
    } else {
      restored.push([el, el.getAttribute("content")]);
    }
    el.setAttribute("content", content);
  };
  setTag("property", "og:title", title);
  setTag("name", "twitter:title", title);
  setTag("property", "og:description", description);
  setTag("name", "twitter:description", description);
  if (canonical) setTag("property", "og:url", canonical);

  // No canonical on noindex pages (per SEO spec).
  if (canonical && !noindex) {
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", "canonical");
      document.head.appendChild(el);
      created.push(el);
    }
    el.setAttribute("href", canonical);
  }

  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  blocks.filter(Boolean).forEach((obj) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo-jsonld", "1");
    s.textContent = JSON.stringify(obj);
    document.head.appendChild(s);
    created.push(s);
  });

  return () => {
    document.title = prevTitle;
    restored.forEach(([el, v]) => el.setAttribute("content", v || ""));
    created.forEach((el) => el.remove());
  };
};

// MusicGroup: image is passed in from the live artist avatar setting, never hardcoded.
export const musicGroupSchema = (image) => {
  const s = {
    "@context": "https://schema.org",
    "@type": "MusicGroup",
    name: "SAMTWO",
    alternateName: ["Bernipk", "Samtu Palouki"],
    url: SITE_URL,
    genre: ["Afropop", "Pop urbaine", "Afrobeat Francophone", "Hip-Hop", "Rap", "Trap"],
    description:
      "SAMTWO (born Samtu Palouki, also known as Bernipk) is a Togolese-American recording artist, rapper, actor, filmmaker, and content creator. Raised in Lome, Togo and based in the United States, where he also serves as a U.S. Army soldier, SAMTWO creates music at the intersection of Afropop, Afrobeat Francophone, Pop urbaine, Hip-Hop, Rap, and Trap, with lyrics in both French and English. His debut single 'Addiction' launched in 2025, accumulating over 200,000 YouTube views and 6 million social media impressions. His catalog includes 'Let's Party' (feat. Svxmmy S.O.S & Becca Flo), 'PERIS,' 'Papa,' 'DEMBELE,' 'Lamine,' and 'Parametres.' SAMTWO distributes his music through his own direct-to-fan subscription platform at samtwoentertainment.com and on YouTube, having previously released through Spotify, Apple Music, and Deezer. Beyond music, he is widely recognized as Bernipk, a viral comedy and short film creator with a large following across French-speaking African and international audiences on Instagram and TikTok.",
    sameAs: [
      "https://www.youtube.com/channel/UCdGX0uVFfUeK9A5kCx0Sdjw",
      "https://www.tiktok.com/@samtwo.official",
      "https://www.instagram.com/samtwo.official",
      "https://x.com/samtwoofficial",
      "https://open.spotify.com/artist/3An0EG0XGNxZ83ONF77Y0F",
      "https://music.apple.com/us/artist/samtwo/1801394818",
      "https://www.deezer.com/en/artist/308426121",
      "https://www.bernipk.com/",
      "https://www.instagram.com/bernipk/",
      "https://www.tiktok.com/@bernipk",
      "https://www.imdb.com/name/nm11801430/",
    ],
  };
  if (image) s.image = image;
  return s;
};

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "SAMTWO",
  url: SITE_URL,
});

export const articleSchema = ({ title, description, image, slug, published, modified }) => {
  const s = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    author: { "@type": "Person", name: "SAMTWO" },
    publisher: { "@type": "Organization", name: "SAMTWO", url: SITE_URL },
    url: `${SITE_URL}/blog/${slug}`,
  };
  if (description) s.description = description;
  if (image) s.image = image;
  if (published) s.datePublished = published;
  if (modified || published) s.dateModified = modified || published;
  return s;
};

export const eventSchema = ({ name, startDate, endDate, description, url }) => {
  const s = {
    "@context": "https://schema.org",
    "@type": "Event",
    name,
    url: url || `${SITE_URL}/`,
    organizer: { "@type": "Person", name: "SAMTWO" },
  };
  if (startDate) s.startDate = startDate;
  if (endDate) s.endDate = endDate;
  if (description) s.description = description;
  return s;
};

export const faqPageSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const productSchema = ({ name, image, description, price, currency, url, availability }) => {
  const s = { "@context": "https://schema.org", "@type": "Product", name };
  if (image) s.image = image;
  if (description) s.description = description;
  if (url) s.url = url;
  if (price != null) {
    s.offers = {
      "@type": "Offer",
      price: String(price),
      priceCurrency: currency || "USD",
      availability: availability || "https://schema.org/InStock",
      url: url || SITE_URL,
    };
  }
  return s;
};

// Seconds -> ISO 8601 duration (PT#M#S) for schema.org duration fields.
export const isoDuration = (sec) => {
  const n = Number(sec);
  if (!n || n <= 0) return undefined;
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `PT${m}M${s}S`;
};

const SAMTWO_ARTIST = { "@type": "MusicGroup", name: "SAMTWO", url: SITE_URL };

// Text comes from translation keys (seo.*), so every new item gets the same pattern
// in the visitor's language, with English as the fallback.
export const seoText = (key, vars) => i18n.t(`seo.${key}`, vars);
// Breadcrumb names reuse the menu labels so they always match the navigation.
export const crumbLabel = (key) => i18n.t(`nav.${key}`);
export const withFeat = (title, featuring) => (featuring ? `${title} (feat. ${featuring})` : title);
export const releaseTypeLabel = (type) => seoText(type === "ep" ? "typeEp" : type === "single" ? "typeSingle" : "typeAlbum");

const WEB_PLATFORMS = ["https://schema.org/DesktopWebPlatform", "https://schema.org/MobileWebPlatform"];
const playAction = (kind, path, locked) => ({
  "@type": kind,
  target: { "@type": "EntryPoint", urlTemplate: canonicalFor(path), actionPlatform: WEB_PLATFORMS },
  expectsAcceptanceOf: { "@type": "Offer", category: locked ? "subscription" : "free" },
});
// Google's paywalled-content markup: the locked panel carries the .seo-paywalled class.
const paywall = (s, locked, selector = ".seo-paywalled") => {
  s.isAccessibleForFree = !locked;
  if (locked) s.hasPart = { "@type": "WebPageElement", isAccessibleForFree: false, cssSelector: selector };
  return s;
};
const isoDate = (d) => (d ? String(d).slice(0, 10) : undefined);
const isHttp = (u) => typeof u === "string" && /^https?:\/\//.test(u);

export const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: canonicalFor(c.path) })),
});

export const musicRecordingSchema = ({ name, image, description, durationSec, path, locked, artistName, sameAs, datePublished, albumPath, albumName }) => {
  const s = {
    "@context": "https://schema.org",
    "@type": "MusicRecording",
    name,
    byArtist: artistName && artistName !== "SAMTWO" ? { "@type": "MusicGroup", name: artistName } : SAMTWO_ARTIST,
    url: canonicalFor(path),
    potentialAction: playAction("ListenAction", path, locked),
  };
  paywall(s, locked);
  if (image) s.image = image;
  if (description) s.description = description;
  const links = (sameAs || []).filter(isHttp);
  if (links.length) s.sameAs = links;
  const d = isoDuration(durationSec);
  if (d) s.duration = d;
  if (isoDate(datePublished)) s.datePublished = isoDate(datePublished);
  if (albumPath) s.inAlbum = { "@type": "MusicAlbum", url: canonicalFor(albumPath), ...(albumName ? { name: albumName } : {}) };
  return s;
};

export const itemListSchema = (name, entries) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  itemListElement: entries.map((e, i) => ({ "@type": "ListItem", position: i + 1, url: canonicalFor(e.path), name: e.name })),
});

const ALBUM_RELEASE = { ep: "https://schema.org/EPRelease", single: "https://schema.org/SingleRelease", album: "https://schema.org/AlbumRelease" };
export const musicAlbumSchema = ({ name, image, description, numTracks, path, locked, releaseType, datePublished, tracks = [] }) => {
  const s = {
    "@context": "https://schema.org",
    "@type": "MusicAlbum",
    name,
    byArtist: SAMTWO_ARTIST,
    url: canonicalFor(path),
    potentialAction: playAction("ListenAction", path, locked),
  };
  paywall(s, locked);
  if (image) s.image = image;
  if (description) s.description = description;
  if (numTracks) s.numTracks = numTracks;
  if (ALBUM_RELEASE[releaseType]) s.albumReleaseType = ALBUM_RELEASE[releaseType];
  if (isoDate(datePublished)) s.datePublished = isoDate(datePublished);
  if (tracks.length) {
    s.track = tracks.map((tr, i) => {
      const t = { "@type": "MusicRecording", position: i + 1, name: tr.title, url: canonicalFor(`/music/track/${tr.id}`) };
      const d = isoDuration(tr.duration_sec);
      if (d) t.duration = d;
      return t;
    });
  }
  return s;
};

export const musicPlaylistSchema = ({ name, image, description, numTracks, path }) => {
  const s = {
    "@context": "https://schema.org",
    "@type": "MusicPlaylist",
    name,
    url: canonicalFor(path),
  };
  if (image) s.image = image;
  if (description) s.description = description;
  if (numTracks) s.numTracks = numTracks;
  return s;
};

export const videoObjectSchema = ({ name, description, thumbnail, durationSec, uploadDate, path, locked, id }) => {
  const s = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name,
    url: canonicalFor(path),
    potentialAction: playAction("WatchAction", path, locked),
  };
  paywall(s, locked, id ? `.seo-paywalled-${id}` : ".seo-paywalled");
  if (description) s.description = description;
  if (thumbnail) s.thumbnailUrl = thumbnail;
  const d = isoDuration(durationSec);
  if (d) s.duration = d;
  if (uploadDate) s.uploadDate = uploadDate;
  return s;
};


// A show renders as a TVSeries whose episodes are TVEpisode parts (hasPart).
// Movies stay on videoObjectSchema. Episode thumbnails/titles come from the show
// payload; access is per episode, so isAccessibleForFree mirrors each lock.
export const tvSeriesSchema = ({ name, description, image, path, episodes = [] }) => {
  const s = {
    "@context": "https://schema.org",
    "@type": "TVSeries",
    name,
    url: canonicalFor(path),
    potentialAction: playAction("WatchAction", path, false),
  };
  if (description) s.description = description;
  if (image) s.image = image;
  if (episodes.length) {
    s.numberOfEpisodes = episodes.length;
    s.hasPart = episodes.map((ep, i) => {
      const part = {
        "@type": "TVEpisode",
        name: ep.title || `Episode ${i + 1}`,
        episodeNumber: i + 1,
        url: canonicalFor(`/videos/${ep.id}`),
        isAccessibleForFree: !ep.locked,
      };
      if (ep.thumbnail) part.image = ep.thumbnail;
      return part;
    });
  }
  return s;
};
